import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Library,
  Globe2,
  Brain,
  CalendarClock,
  Video,
  Play,
  Users,
  ArrowRight,
} from 'lucide-react';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';

const redGradient =
  'linear-gradient(135deg,rgba(255,255,255,.15),transparent 50%,rgba(0,0,0,.15)), #b30000';

const Eyebrow: React.FC<{ children: React.ReactNode; light?: boolean }> = ({ children, light }) => (
  <p className={`text-xs font-semibold tracking-[0.12em] uppercase mb-3 ${light ? 'text-white/60' : 'text-[#970000]'}`}>
    {children}
  </p>
);

const ArabischLernenPage = () => {
  const benefits = [
    { icon: BookOpen, h: 'Den Quran direkt verstehen', t: 'Ohne Übersetzung lesen, was du betest und rezitierst – das ist das Ziel vieler unserer Schüler.' },
    { icon: Library, h: 'Klassische Texte lesen', t: 'Islamische Bücher, Hadith-Sammlungen und arabische Literatur im Original zugänglich machen.' },
    { icon: Globe2, h: 'Eine lebendige Weltsprache sprechen', t: 'Hocharabisch wird in 22 Ländern verstanden – in Medien, Bildung und Wissenschaft.' },
    { icon: Brain, h: 'Kognitive Vorteile', t: 'Arabisch lernen trainiert das Gehirn durch sein einzigartiges Wurzelsystem und stärkt analytisches Denken.' },
  ];

  const milestones = [
    { time: '3–5 Monate', goal: 'Das arabische Alphabet sicher lesen und schreiben' },
    { time: '1–1,5 Jahre', goal: 'Grundlegende Grammatik und einfache Texte verstehen' },
    { time: '2–3 Jahre', goal: 'Quran-Texte mit Verständnis lesen' },
    { time: '4–5 Jahre', goal: 'Selbstständig arabische Originaltexte lesen' },
  ];

  const beginnerSteps = [
    { h: 'Das arabische Alphabet lernen', t: 'Alle 28 Buchstaben, ihre Formen am Wortanfang, in der Mitte und am Ende – plus grundlegende Ausspracheregeln. Das ist unser Buchstabenkurs (Level 1).' },
    { h: 'Grammatik-Grundlagen aufbauen', t: 'Arabisch ist eine hochstrukturierte Sprache. Das Wurzelsystem macht es leichter als erwartet – wenn man es richtig vermittelt bekommt. Das ist Level 2 mit Madina Buch 1.' },
    { h: 'Wortschatz durch Texte aufbauen', t: 'Nicht durch Vokabellisten, sondern durch echte arabische Texte und Gespräche – kontextuell und nachhaltig.' },
    { h: 'Regelmäßig üben', t: 'Zwei Stunden pro Woche Live-Unterricht plus tägliche kurze Wiederholungen mit unserer Lern-App reichen, um stetig Fortschritte zu machen.' },
  ];

  const onlineBenefits = [
    { icon: CalendarClock, h: 'Wähle deinen Zeitplan', t: 'Du nimmst von zuhause aus teil – kein Pendeln, kein fester Ort. Ideal für Berufstätige, Eltern und Studierende.' },
    { icon: Video, h: 'Echte Live-Interaktion', t: 'Unsere Kurse sind keine Videokurse. Du lernst live mit Lehrer und Mitschülern – Fragen, Korrekturen, Gespräche in Echtzeit.' },
    { icon: Play, h: 'Aufzeichnungen inklusive', t: 'Jede Session wird aufgezeichnet. Verpasst du eine Einheit, holst du sie nach. Kein Lerninhalt geht verloren.' },
    { icon: Users, h: 'Kleine Gruppen weltweit', t: 'Unsere Schüler kommen aus Deutschland, Österreich, der Schweiz und darüber hinaus – verbunden durch die Sprache.' },
  ];

  const levels = [
    { l: 'Level 1', t: 'Buchstabenkurs', d: 'Arabisches Alphabet lesen, schreiben, aussprechen – Grundlage für alles.' },
    { l: 'Level 2', t: 'Grundlagenkurs', d: 'Erste Grammatik und Wortschatz nach Madina Buch 1 und Arabya Bayna Yadayk 1.' },
    { l: 'Level 3', t: 'Aufbaukurs', d: 'Grammatik und Textverständnis vertiefen – Madina Buch 2.' },
    { l: 'Level 4', t: 'Fortgeschrittenenkurs', d: 'Komplexe Grammatik und Texte analysieren – Madina Buch 3.' },
    { l: 'Level 5', t: 'Expertenkurs', d: 'Tiefes Grammatikverständnis nach Al-Ajrumiyyah.' },
    { l: 'Level 6', t: 'Meisterstufe', d: 'Selbstständiges Lesen arabischer Originaltexte.' },
  ];

  return (
    <>
      <SEO
        title="Arabisch lernen: Einstieg, Dauer & der richtige Kurs – Leitfaden"
        description="Arabisch lernen leicht gemacht: Wie du richtig startest, wie lange es dauert und welcher Kurs zu dir passt. Der komplette Leitfaden – vom Alphabet bis zum Originaltext."
        keywords="Arabisch lernen, Arabisch online lernen, Arabischkurs online, Arabisch lernen für Anfänger, Arabisch lernen wie lange, Hocharabisch lernen, Arabisch lernen Deutsch, Arabisch Kurs, Arabischunterricht online, Arabisch Alphabet lernen"
        url="/arabisch-lernen"
      />
      <Header />

      {/* Hero — mit dezentem Grid-Hintergrund wie auf der Startseite */}
      <section className="relative bg-white overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div
            className="absolute inset-0 opacity-50"
            style={{
              backgroundImage:
                'linear-gradient(to right,rgba(26,6,6,.06) 1px,transparent 1px),linear-gradient(to bottom,rgba(26,6,6,.06) 1px,transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/50 to-white" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="max-w-3xl">
            <Eyebrow>Arabisch lernen online</Eyebrow>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-[-0.04em] leading-[1.05] text-[#1a0606] mb-6">
              Arabisch lernen:<br />
              <em className="not-italic oblique text-[#970000]">so gelingt</em><br />
              dein Einstieg.
            </h1>
            <p className="text-[16px] md:text-[18px] leading-relaxed text-gray-500 mb-8 max-w-2xl">
              Der komplette Leitfaden: Warum sich Arabisch lohnt, wie lange es wirklich dauert,
              womit Anfänger starten sollten – und welcher Kurs dich vom Alphabet bis zum
              selbstständigen Lesen bringt.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/anmelden"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 font-bold text-[14.5px] text-white shadow-[0_8px_20px_-8px_rgba(151,0,0,0.35)] transition-all duration-150 hover:brightness-110 hover:-translate-y-px"
                style={{ background: redGradient }}
              >
                ▶ Jetzt kostenlos anmelden
              </Link>
              <a
                href="#warum-arabisch-lernen"
                className="inline-flex items-center gap-2 rounded-full border-[1.5px] border-[#1a0606]/[0.18] bg-white/70 px-6 py-3.5 font-bold text-[14.5px] text-[#1a0606] transition-all duration-150 hover:border-[#970000] hover:text-[#970000]"
              >
                Mehr erfahren →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Warum Arabisch lernen */}
      <section id="warum-arabisch-lernen" className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10 md:mb-12">
            <Eyebrow>Warum Arabisch</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.04em] leading-[1.1] text-gray-900">
              Warum <span className="oblique font-extrabold text-[#970000]">Arabisch lernen</span>?
            </h2>
            <p className="mt-4 text-[15px] md:text-[17px] leading-relaxed text-gray-500">
              Arabisch ist mit über 400 Millionen Muttersprachlern eine der meistgesprochenen Sprachen der Welt –
              und die Sprache des Qurans, der klassischen Literatur und der gesamten islamischen Wissenschaft.
              Wer Arabisch lernt, öffnet sich Zugang zu einer der reichsten Kulturen der Menschheitsgeschichte.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {benefits.map(({ icon: Icon, h, t }) => (
              <div key={h} className="rounded-2xl border border-gray-100 bg-white p-5 hover:border-[#970000]/30 transition-all duration-200">
                <div className="w-9 h-9 rounded-xl bg-[#970000]/[0.08] flex items-center justify-center text-[#970000] mb-4">
                  <Icon className="h-4 w-4" />
                </div>
                <h3 className="text-[15px] font-semibold text-gray-900 mb-1.5">{h}</h3>
                <p className="text-[13px] text-gray-500 leading-relaxed">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wie lange dauert es — Split mit rotem Timeline-Panel wie im Über-uns-Block */}
      <section id="wie-lange-arabisch-lernen" className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 items-start">
            <div>
              <Eyebrow>Realistischer Zeitplan</Eyebrow>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.04em] leading-[1.15] text-gray-900 mb-5">
                Wie lange dauert es, <span className="oblique font-extrabold text-[#970000]">Arabisch zu lernen</span>?
              </h2>
              <div className="space-y-4 text-[15px] leading-[1.8] text-gray-500">
                <p>
                  Die ehrliche Antwort: es kommt auf deinen Einsatz und dein Ziel an. Mit unserem strukturierten
                  Kursweg ist nebenstehender Fortschritt realistisch.
                </p>
                <p>
                  Mit zwei Live-Sessions pro Woche und regelmäßigem Selbststudium liegst du im Schnitt unserer
                  erfolgreichsten Schüler.
                </p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-2xl bg-[#b30000] p-5 md:p-8 text-white">
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/15 via-transparent to-black/15" />
              <div className="relative">
                <Eyebrow light>Dein Fortschritt</Eyebrow>
                <h3 className="text-xl md:text-2xl font-bold tracking-[-0.03em] leading-[1.25] text-white mb-8">
                  Was du wann erreichst
                </h3>
                <div className="space-y-0">
                  {milestones.map(({ time, goal }, index) => (
                    <div key={time} className="flex items-start gap-4">
                      <div className="flex flex-col items-center pt-1 shrink-0">
                        <div className="h-2.5 w-2.5 rounded-full bg-white" />
                        {index !== milestones.length - 1 && (
                          <div className="mt-1 mb-1 h-12 w-px bg-white/25" />
                        )}
                      </div>
                      <div className={`pb-1 ${index !== milestones.length - 1 ? 'mb-1' : ''}`}>
                        <p className="text-[14px] font-semibold text-white mb-1">{time}</p>
                        <p className="text-[13px] text-white/70 leading-relaxed">{goal}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Arabisch lernen für Anfänger */}
      <section id="arabisch-lernen-anfaenger" className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10 md:mb-12">
            <Eyebrow>Für Anfänger</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.04em] leading-[1.1] text-gray-900">
              Arabisch lernen für Anfänger – <span className="oblique font-extrabold text-[#970000]">wo anfangen?</span>
            </h2>
            <p className="mt-4 text-[15px] md:text-[17px] leading-relaxed text-gray-500">
              Viele beginnen mit dem falschen Schritt: Sie versuchen Wörter auswendig zu lernen, ohne das
              Alphabet zu kennen. Das führt zu Frustration. Der richtige Einstieg ist systematisch:
            </p>
          </div>
          <ol className="grid md:grid-cols-2 gap-4 list-none">
            {beginnerSteps.map(({ h, t }, i) => (
              <li key={h} className="rounded-2xl border border-gray-100 bg-white p-5 hover:border-[#970000]/30 transition-all duration-200">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-7 h-7 rounded-full bg-[#970000] text-white text-[13px] font-bold flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <h3 className="text-[15px] font-semibold text-gray-900">{h}</h3>
                </div>
                <p className="text-[13px] text-gray-500 leading-relaxed">{t}</p>
              </li>
            ))}
          </ol>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <Link
              to="/arabisch-alphabet-lernen"
              className="group rounded-2xl border border-gray-200 bg-white p-5 hover:border-[#970000]/50 hover:shadow-[0_8px_30px_rgba(151,0,0,0.10)] transition-all duration-200"
            >
              <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-[#970000] mb-2">Vertiefung</p>
              <h3 className="text-[16px] font-bold text-gray-900 mb-1">Arabisches Alphabet lernen</h3>
              <p className="text-[13px] text-gray-500 leading-relaxed mb-4">Alle 28 Buchstaben, ihre Aussprache und der schnellste Weg zum flüssigen Lesen.</p>
              <span className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#970000]">
                Zum Alphabet-Guide
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
              </span>
            </Link>
            <Link
              to="/quran-arabisch-lernen"
              className="group rounded-2xl border border-gray-200 bg-white p-5 hover:border-[#970000]/50 hover:shadow-[0_8px_30px_rgba(151,0,0,0.10)] transition-all duration-200"
            >
              <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-[#970000] mb-2">Vertiefung</p>
              <h3 className="text-[16px] font-bold text-gray-900 mb-1">Quran-Arabisch lernen</h3>
              <p className="text-[13px] text-gray-500 leading-relaxed mb-4">Der Lernweg, um den Quran ohne Übersetzung im Original zu verstehen.</p>
              <span className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#970000]">
                Zum Quran-Guide
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Arabisch online lernen */}
      <section id="arabisch-online-lernen" className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10 md:mb-12">
            <Eyebrow>Online-Unterricht</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.04em] leading-[1.1] text-gray-900">
              Arabisch online lernen – <span className="oblique font-extrabold text-[#970000]">geht das wirklich?</span>
            </h2>
            <p className="mt-4 text-[15px] md:text-[17px] leading-relaxed text-gray-500">
              Ja – und für viele Lernende funktioniert Arabisch online lernen sogar besser als Präsenzunterricht.
              Das liegt an der Flexibilität, der Gruppenauswahl und der Möglichkeit, alles aufzuzeichnen.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {onlineBenefits.map(({ icon: Icon, h, t }) => (
              <div key={h} className="rounded-2xl border border-gray-100 bg-white p-5 hover:border-[#970000]/30 transition-all duration-200">
                <div className="w-9 h-9 rounded-xl bg-[#970000]/[0.08] flex items-center justify-center text-[#970000] mb-4">
                  <Icon className="h-4 w-4" />
                </div>
                <h3 className="text-[15px] font-semibold text-gray-900 mb-1.5">{h}</h3>
                <p className="text-[13px] text-gray-500 leading-relaxed">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kursübersicht — Karten im Stil des Kurs-Karussells */}
      <section id="arabisch-kurse-uebersicht" className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10 md:mb-12">
            <Eyebrow>Der Lernweg</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.04em] leading-[1.1] text-gray-900">
              Unsere <span className="oblique font-extrabold text-[#970000]">Arabischkurse online</span> im Überblick
            </h2>
            <p className="mt-4 text-[15px] md:text-[17px] leading-relaxed text-gray-500">
              Sechs aufeinander aufbauende Kurse – vom Alphabet bis zu arabischen Originaltexten. Jeder Kurs
              baut auf dem vorherigen auf und hat ein klares Lernziel.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {levels.map(({ l, t, d }) => (
              <Link
                key={l}
                to="/?scroll=kurse"
                className="group rounded-2xl border border-gray-200 bg-white p-5 hover:border-[#970000]/50 hover:shadow-[0_8px_30px_rgba(151,0,0,0.10)] transition-all duration-200"
              >
                <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-[#970000] mb-2">{l}</p>
                <h3 className="text-[16px] font-bold text-gray-900 mb-1">{t}</h3>
                <p className="text-[13px] text-gray-500 leading-relaxed mb-4">{d}</p>
                <span className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#970000]">
                  Kursdetails
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/anmelden"
              className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold text-[15px] text-white shadow-[0_8px_20px_-8px_rgba(151,0,0,0.35)] transition-all duration-150 hover:brightness-110 hover:-translate-y-px"
              style={{ background: redGradient }}
            >
              Jetzt zum richtigen Kurs anmelden →
            </Link>
          </div>
        </div>
      </section>

      {/* Hocharabisch vs. Dialekt — Split mit roter Empfehlungs-Karte */}
      <section id="hocharabisch-lernen" className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6 md:gap-8 items-start">
            <div>
              <Eyebrow>Hocharabisch vs. Dialekt</Eyebrow>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.04em] leading-[1.15] text-gray-900 mb-5">
                Welches Arabisch lernen – <span className="oblique font-extrabold text-[#970000]">Hocharabisch oder Dialekt?</span>
              </h2>
              <div className="space-y-4 text-[15px] leading-[1.8] text-gray-500">
                <p>
                  Wir lehren ausschließlich <strong className="font-semibold text-gray-700">Hocharabisch (Fuṣḥā)</strong> – die Standardsprache,
                  die in der gesamten arabischen Welt verstanden wird. Das ist die Sprache des Qurans, der Medien,
                  der Wissenschaft und der klassischen Literatur.
                </p>
                <p>
                  Dialekte unterscheiden sich von Land zu Land stark. Ägyptisches Arabisch klingt anders als
                  Levantinisch oder Marokkanisch. Hocharabisch hingegen ist überall verständlich und bildet die
                  Basis für das Erlernen jedes Dialekts.
                </p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-2xl bg-[#b30000] p-6 md:p-8 text-white">
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/15 via-transparent to-black/15" />
              <div className="relative">
                <Eyebrow light>Unsere Empfehlung</Eyebrow>
                <p className="text-[17px] md:text-[19px] font-bold tracking-[-0.02em] leading-snug text-white mb-6">
                  Wer Arabisch lernen will – für den Quran, Bücher oder als Basis – beginnt mit Hocharabisch.
                </p>
                <Link
                  to="/anmelden"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[13px] font-semibold text-[#970000] transition-colors duration-200 hover:bg-[#f5f0ef]"
                >
                  <Play className="h-3.5 w-3.5 fill-[#970000]" />
                  Mit Level 1 starten
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA — Banner im Stil des Erfolgs-Banners */}
      <section className="relative py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-[22px] bg-[#b30000] px-8 py-10 md:px-12 md:py-12">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/15 via-transparent to-black/15" />
            <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div className="max-w-xl">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-[1.05] tracking-[-0.03em] text-white">
                  Bereit, <em className="not-italic oblique">Arabisch</em>
                  <br />
                  zu lernen?
                </h2>
                <p className="mt-4 text-[15px] text-white/70 font-normal">
                  Melde dich jetzt an – kostenlos und unverbindlich. Wir finden gemeinsam den richtigen Kurs für dich.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row items-start sm:items-center gap-3 shrink-0">
                <Link
                  to="/anmelden"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-[15px] font-semibold text-[#970000] transition-colors duration-200 hover:bg-[#f5f0ef]"
                >
                  <Play className="h-3.5 w-3.5 fill-[#970000]" />
                  Jetzt kostenlos anmelden
                </Link>
                <Link
                  to="/#faq"
                  className="inline-flex items-center justify-center rounded-full border border-white/30 px-6 py-3 text-[15px] font-semibold text-white transition-colors duration-200 hover:bg-white/10"
                >
                  Häufige Fragen
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ArabischLernenPage;
